import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import {
    IAccessRightFilterCollection,
    IAccessRightSelectionFiltersCollection,
    ILightWeightObjectCollection,
} from '@fi-shared/models';
import { IAvailableOnboardingStepElement } from './fi-onboarding';
import { ObjectTypes } from '@fi-app/shared/types/object-type';
import { IItemsListDataFetchResult } from '@fi-shared/fi-items-list/fi-items-list';
import { IOnboardingEditItem } from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding';
import { cloneDeep } from '@fi-app/shared/utils';

export interface IOnboardingElementsCacheEntry {
    items: IAvailableOnboardingStepElement[];
    totalCount: number;
    hasMore: boolean;
    lightWeightObjectsForLocalSearch?: ILightWeightObjectCollection;
}

export interface IOnboardingElementsCache {
    [objectType: number]: {
        [sortDirection: string]: IOnboardingElementsCacheEntry;
    };
}

export enum FiOnboardingBuilderTabIndex {
    OnboardingEditor = 0,
    SettingsSharing = 1,
}

export enum FiOnboardingBuilderSharingSettingsStep {
    Share = 0,
    Category = 1,
    Activate = 2,
}

@Injectable()
export class FiOnboardingBuilderStoreService {
    private _highlightedStepElementId = signal<string>(undefined);
    readonly highlightedStepElementId = this._highlightedStepElementId.asReadonly();
    setHighlightedStepElementId(id: string) {
        this._highlightedStepElementId.set(id);
    }

    private _initialOnboarding: IOnboardingEditItem | undefined;
    setInitialOnboarding(onboarding: IOnboardingEditItem) {
        this._initialOnboarding = cloneDeep(onboarding);
    }
    getInitialOnboarding(): IOnboardingEditItem {
        return this._initialOnboarding;
    }

    private _selectedLanguageId = signal<number>(undefined);
    readonly selectedLanguageId = this._selectedLanguageId.asReadonly();
    setSelectedLanguageId(id: number) {
        this._selectedLanguageId.set(id);
    }

    private _selectedTabIndex = signal<FiOnboardingBuilderTabIndex>(FiOnboardingBuilderTabIndex.OnboardingEditor);
    readonly selectedTabIndex = this._selectedTabIndex.asReadonly();
    setSelectedTabIndex(index: FiOnboardingBuilderTabIndex): void {
        this._selectedTabIndex.set(index);
    }

    private _selectedSettingsStep = signal<FiOnboardingBuilderSharingSettingsStep>(
        FiOnboardingBuilderSharingSettingsStep.Share,
    );
    readonly selectedSettingsStep = this._selectedSettingsStep.asReadonly();
    setSelectedSettingsStep(step: FiOnboardingBuilderSharingSettingsStep): void {
        this._selectedSettingsStep.set(step);
    }

    private _onboardingAccessRightFilterCollection = signal<IAccessRightFilterCollection | undefined>(undefined);
    readonly onboardingAccessRightFilterCollection = this._onboardingAccessRightFilterCollection.asReadonly();
    setOnboardingAccessRightFilterCollection(value: IAccessRightFilterCollection): void {
        this._onboardingAccessRightFilterCollection.set(value);
    }

    private _onboardingQuickSelectionFilters = signal<IAccessRightSelectionFiltersCollection[]>([]);
    readonly onboardingQuickSelectionFilters = this._onboardingQuickSelectionFilters.asReadonly();
    setOnboardingQuickSelectionFilters(value: IAccessRightSelectionFiltersCollection[]): void {
        this._onboardingQuickSelectionFilters.set(value);
    }

    private _selectedFiltersCollection = signal<IAccessRightSelectionFiltersCollection | undefined>(undefined);
    readonly selectedFiltersCollection = this._selectedFiltersCollection.asReadonly();
    setSelectedFiltersCollection(value: IAccessRightSelectionFiltersCollection): void {
        this._selectedFiltersCollection.set(value);
    }

    private _formSubmitted = signal<number>(0);
    readonly formSubmitted = this._formSubmitted.asReadonly();
    onFormSubmit(): void {
        this._formSubmitted.update((value) => value + 1);
    }

    private _updateFormValidity = signal<number>(0);
    readonly updateFormValidity = this._updateFormValidity.asReadonly();
    private triggerFormValidityUpdate(): void {
        this._updateFormValidity.update((value) => value + 1);
    }

    private _currentOnboarding = signal<IOnboardingEditItem | undefined>(undefined);
    readonly currentOnboarding = this._currentOnboarding.asReadonly();
    setCurrentOnboarding(onboarding: IOnboardingEditItem): void {
        this._currentOnboarding.set(onboarding);
    }
    updateCurrentOnboarding(updater: (current: IOnboardingEditItem) => IOnboardingEditItem): void {
        this._currentOnboarding.update(updater);
        if (this.formSubmitted() > 0) {
            this.triggerFormValidityUpdate();
        }
    }

    private _onboardingQuickSelectionFiltersSave = signal<number>(0);
    readonly onboardingQuickSelectionFiltersSave = this._onboardingQuickSelectionFiltersSave.asReadonly();
    setOnboardingQuickSelectionFiltersSave(): void {
        this._onboardingQuickSelectionFiltersSave.update((value) => value + 1);
    }

    private _onboardingElementsCache: IOnboardingElementsCache = {};

    getCachedElements(objectType: ObjectTypes, sortDirection: boolean): IOnboardingElementsCacheEntry | undefined {
        const cacheKey = sortDirection.toString();
        return this._onboardingElementsCache[objectType]?.[cacheKey];
    }

    setCachedElements(
        objectType: ObjectTypes,
        sortDirection: boolean,
        result: IItemsListDataFetchResult<IAvailableOnboardingStepElement>,
        offset: number = 0,
    ): void {
        if (!this._onboardingElementsCache[objectType]) {
            this._onboardingElementsCache[objectType] = {};
        }
        const cacheKey = sortDirection.toString();
        const existingCache = this._onboardingElementsCache[objectType][cacheKey];

        if (offset === 0 || !existingCache) {
            this._onboardingElementsCache[objectType][cacheKey] = {
                items: [...result.Items],
                totalCount: result.TotalCount,
                hasMore: result.Items.length < result.TotalCount,
                lightWeightObjectsForLocalSearch: result.LightWeightObjectsForLocalSearch,
            };
        } else {
            const newItems = [...existingCache.items, ...result.Items];
            this._onboardingElementsCache[objectType][cacheKey] = {
                items: newItems,
                totalCount: result.TotalCount,
                hasMore: newItems.length < result.TotalCount,
                lightWeightObjectsForLocalSearch:
                    existingCache.lightWeightObjectsForLocalSearch || result.LightWeightObjectsForLocalSearch,
            };
        }
    }

    clearElementsCache(objectType?: ObjectTypes): void {
        if (objectType) {
            delete this._onboardingElementsCache[objectType];
        } else {
            this._onboardingElementsCache = {};
        }
    }

    resetState(): void {
        this.setSelectedTabIndex(FiOnboardingBuilderTabIndex.OnboardingEditor);
        this.setSelectedSettingsStep(FiOnboardingBuilderSharingSettingsStep.Share);
        this.setOnboardingAccessRightFilterCollection(undefined);
        this.setOnboardingQuickSelectionFilters([]);
        this.setSelectedFiltersCollection(undefined);
        this.setCurrentOnboarding(undefined);
        this.setInitialOnboarding(undefined);
        this.setSelectedLanguageId(undefined);
        this._formSubmitted.set(0);
        this._onboardingQuickSelectionFiltersSave.set(0);
        this._highlightedStepElementId.set(undefined);
    }
}
